import { useContext, useEffect, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { NVButton } from '@nv/basic-components'
import { TextField } from '@nv/basic-components'
import { Loader } from '@nv/basic-components'
import { ErrorLabel } from '@nv/components'
import { LoginFlowContext, useAdminUserContext, useConfigurationContext } from '@nv/contexts'
import { VoterOnboardingContext } from '@nv/contexts'
import { AdminVoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './AdminLogin.module.scss'
import '@djthoms/pretty-checkbox'

const initialValues = {
  username: '',
  password: '',
}

export function AdminLogin() {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const navigate = useNavigate()
  const location = useLocation()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const LOGIN_CONTEXT = useContext(LoginFlowContext)
  const { setAuthMethod } = useContext(VoterOnboardingContext)
  const [errorMessage, setErrorMessage] = useState('')
  const { setAdminUser } = useAdminUserContext()
  const { includeWebAuthnLoginAdmin } = useConfigurationContext()
  const [loginType, setLoginType] = useState('')

  const loginSchema = Yup.object().shape({
    username: Yup.string().required(t('auth.login.validations.username.admin')),
    password: Yup.string().required(t('auth.login.validations.password')),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setTimeout(async () => {
        try {
          const userInput = {
            username: values.username,
            password: values.password,
          }
          if (!executeRecaptcha) {
            //console.log('Execute recaptcha not yet available')
            return
          }
          const token = await executeRecaptcha('admin_login')

          let redirect = false
          if (location.pathname.includes('redirect')) {
            redirect = true
          }
          sessionStorage.setItem('redirect', redirect ? 'true' : '')
          const resp = await AdminVoterAuthService.login(userInput.username, userInput.password, token, currentLanguage)

          if (resp?.success) {
            if (resp.user) {
              setAdminUser(resp.user)
            }
            if (resp.onboarding) {
              LOGIN_CONTEXT.setLoginFlow('Admin Onboarding')
              setAuthMethod('SMS')
              navigate('/admin/auth/verify')
            } else if (resp.passwordResetRequired) {
              setAuthMethod('Authapp')
              LOGIN_CONTEXT.setLoginFlow('Admin Forgot Password')
              navigate('/admin/auth/verify')
            } else {
              setAuthMethod('Authapp')
              LOGIN_CONTEXT.setLoginFlow('Admin Login')
              navigate('/admin/auth/verify')
            }

            setLoading(false)
            setError(false)
          } else {
            setError(true)
            // console.error(resp.error?.message)
            switch (resp.error?.message) {
              case 'Invalid user credentials':
                setErrorMessage(t('form.validations.invalidPwOrUser'))
                break
              case 'session error: only one session per user':
                setErrorMessage(t('auth.login.errors.session'))
                break
              case 'Account locked.':
                setErrorMessage(t('auth.login.errors.accountLocked'))
                break
              case 'recaptcha error':
                setErrorMessage(t('auth.login.errors.recaptcha'))
                break
              default:
                setErrorMessage(t('auth.login.errors.generic'))
                break
            }
          }

          setLoading(false)
        } catch (error) {
          //console.log(error)
          setError(true)
          setErrorMessage(t('auth.login.errors.generic'))
          setLoading(false) // Ensure loading state is reset on error
        }
      }, 10)
    },
  })

  useEffect(() => {
    if (errorMessage) setErrorMessage('')
  }, [formik.values.password])

  if (loginType === '' && includeWebAuthnLoginAdmin) {
    return (
      <div className={styles['container']}>
        <div className={styles['form']}>
          <div className={styles['form__section']}>
            <NVButton
              height={50}
              classes={{ container: styles['button'] }}
              type='button'
              text={t('Mail-In Login')}
              ariaLabel={t('Mail-In Login')}
              onClick={() => setLoginType('password')}
            />
            <NVButton
              height={50}
              classes={{ container: styles['button'] }}
              type='button'
              text={t('DRO Login')}
              ariaLabel={t('DRO Login')}
              onClick={() => KeycloakAdminService.doLogin()}
            />
          </div>
        </div>
      </div>
    )
  } else
    return (
      <div className={styles['container']}>
        <form
          className={styles['form']}
          onSubmit={(e) => {
            e.preventDefault()
            formik.submitForm()
          }}
        >
          <span className={styles['title']}>{t('pages.login')}</span>

          <div className={styles['form__section']}>
            <TextField
              classes={{ container: styles['username-input'] }}
              formik={formik}
              type='text'
              field='username'
              placeholder={t('form.labels.username')}
              label={t('form.labels.username')}
              isRequired={true}
            />
          </div>

          <TextField
            classes={{ container: styles['password-input'] }}
            formik={formik}
            type='password'
            field='password'
            placeholder={t('auth.login.labels.password')}
            label={t('auth.login.labels.password')}
            isRequired={true}
            isPassword={true}
          />

          <div className={styles['forgot-pass']}>
            {t('auth.login.forgotPassword')}
            <NVButton
              variant='text'
              classes={{ container: styles['forgot-pass__link'] }}
              onClick={() => navigate('/admin/auth/reset-password')}
              text={t('buttons.click')}
            />
          </div>

          {error && <ErrorLabel label={errorMessage} />}
          {loading ? (
            <Loader />
          ) : (
            <NVButton
              height={50}
              classes={{ container: styles['button'] }}
              type='submit'
              text={t('buttons.next')}
              ariaLabel={t('buttons.login')}
            />
          )}
        </form>
      </div>
    )
}
